
import React from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle, Book, DollarSign, Tag } from "lucide-react";

const CollectorTipsPage: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl font-bold mb-6">Collector Tips</h1>
          <p className="text-lg text-muted-foreground mb-8">
            Essential advice for both new and experienced Pokémon card collectors to maximize your collection's value and enjoyment.
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-blue-100 to-blue-50 border-b">
                <div className="flex items-center gap-3">
                  <Book className="h-5 w-5 text-blue-700" />
                  <CardTitle>Getting Started</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="pb-4 border-b border-dashed">
                    <h3 className="text-lg font-medium mb-2">Define Your Collection Goals</h3>
                    <p className="text-sm text-gray-600">
                      Decide what you want to collect - specific Pokémon, complete sets, artwork by certain illustrators, 
                      or a particular era. Having clear goals helps focus your collecting strategy.
                    </p>
                  </div>
                  
                  <div className="pb-4 border-b border-dashed">
                    <h3 className="text-lg font-medium mb-2">Learn About Card Sets</h3>
                    <p className="text-sm text-gray-600">
                      Familiarize yourself with different sets, their release dates, and relative rarity. 
                      Understanding set symbols and card numbering systems helps identify what you're looking at.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium mb-2">Start Small</h3>
                    <p className="text-sm text-gray-600">
                      Begin with modern, accessible sets before diving into vintage collections. 
                      This helps you learn the hobby without making costly mistakes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-green-100 to-green-50 border-b">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-700" />
                  <CardTitle>Card Protection</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Essential Supplies</h3>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-green-700 text-xs font-bold">1</span>
                        </div>
                        <span><strong>Penny Sleeves:</strong> Basic protection for all cards</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-green-700 text-xs font-bold">2</span>
                        </div>
                        <span><strong>Top Loaders:</strong> Rigid protection for valuable cards</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-green-700 text-xs font-bold">3</span>
                        </div>
                        <span><strong>Card Binders:</strong> For display and organization</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-green-700 text-xs font-bold">4</span>
                        </div>
                        <span><strong>Card Savers:</strong> For cards being sent for grading</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium">Handling Best Practices</h3>
                    <ul className="space-y-3 text-sm">
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-red-700 text-xs font-bold">✕</span>
                        </div>
                        <span>Never touch the surface of cards with bare fingers</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-red-700 text-xs font-bold">✕</span>
                        </div>
                        <span>Avoid exposure to direct sunlight and humidity</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-green-700 text-xs font-bold">✓</span>
                        </div>
                        <span>Store cards in a cool, dry place</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
                          <span className="text-green-700 text-xs font-bold">✓</span>
                        </div>
                        <span>Use side loading binder pages to prevent cards from falling out</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-amber-100 to-amber-50 border-b">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-amber-700" />
                  <CardTitle>Investment Tips</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-6">
                  <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100">
                    <h3 className="font-semibold mb-2">Research Market Trends</h3>
                    <p className="text-sm">
                      Monitor sales data on platforms like eBay, TCGPlayer, and auction sites. 
                      Understanding price history helps inform purchasing decisions.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100">
                    <h3 className="font-semibold mb-2">Diversify Your Collection</h3>
                    <p className="text-sm">
                      Don't put all your resources into a single card or set. Spread investments 
                      across different eras and types of cards.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100">
                    <h3 className="font-semibold mb-2">Consider Professional Grading</h3>
                    <p className="text-sm">
                      For potentially valuable cards, professional grading services like PSA, BGS, or CGC 
                      can significantly increase value and marketability.
                    </p>
                  </div>
                  
                  <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100">
                    <h3 className="font-semibold mb-2">Be Patient</h3>
                    <p className="text-sm">
                      Card values fluctuate. Collector markets often follow cycles, so be prepared 
                      to hold cards for years to realize maximum potential.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-red-100 to-red-50 border-b">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-700" />
                  <CardTitle>Avoiding Counterfeits</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <p className="text-sm">
                    As the value of Pokémon cards has increased, so has the prevalence of counterfeits. 
                    Here's how to protect yourself:
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-lg bg-red-50/50 border border-red-100">
                      <h3 className="font-semibold mb-2">Light Test</h3>
                      <p className="text-xs">
                        Hold the card up to a light source. Genuine cards will not let much light through, 
                        while fakes often appear more transparent.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-red-50/50 border border-red-100">
                      <h3 className="font-semibold mb-2">Color & Print Quality</h3>
                      <p className="text-xs">
                        Authentic cards have vibrant, consistent colors. Counterfeit cards often have 
                        dull colors, blurry text, or poor print alignment.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-red-50/50 border border-red-100">
                      <h3 className="font-semibold mb-2">Paper Quality</h3>
                      <p className="text-xs">
                        Real Pokémon cards have a specific feel and thickness. Fakes often feel 
                        too thin, too thick, or have a different texture.
                      </p>
                    </div>
                    
                    <div className="p-4 rounded-lg bg-red-50/50 border border-red-100">
                      <h3 className="font-semibold mb-2">Trusted Sources</h3>
                      <p className="text-xs">
                        Purchase from reputable sellers with positive feedback histories. Be especially 
                        cautious with deals that seem too good to be true.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-purple-100 to-purple-50 border-b">
                <div className="flex items-center gap-3">
                  <Tag className="h-5 w-5 text-purple-700" />
                  <CardTitle>Community Engagement</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <p className="text-sm mb-6">
                  Connecting with fellow collectors can enhance your experience and knowledge:
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3">Online Communities</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Reddit's r/PokemonTCG</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Discord collector groups</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Instagram collector community</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>YouTube channels focusing on card collecting</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3">Local Events</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Card shop tournaments</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Trading meetups</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Collector conventions</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span>Pre-release events for new sets</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </PageContainer>
  );
};

export default CollectorTipsPage;
